/**
  * @class meleFile
  * @description
  *
  * # Purpose
  * Implements `<mele-file>` custom web element.
  *
  * # Use
  * Create element in HTML:
  * ```
  * parentEl.innerHTML += '<mele-file></mele-file>';
  * ```
  * Create element in JavaScript:
  * ```
  * parentEl.appendChildren({
  *   tag: 'mele-file',
  *   ... other attributes ...
  * })
  * ```
  *
  * # Implementation notes
  * - Preview elements (_elImg or _elIcon) are created on-demand in blobChanged()
  * - Both image and icon previews have click handlers attached
  * - PDF files show an icon but open in modal on click
  *
  */

import meleElement from 'meleElement'
const moment = require('moment')

const PROPERTIES = _.extend({}, {
  name: { type: 'string', description: 'file name' },
  mime: { type: 'string', description: 'file mime type' },
  size: { type: 'string', description: 'file size' },
  blob: { type: 'string', description: 'blob used to generate dimensions' },
  timestamp: { type: 'number', description: 'datetime of creation in seconds' },

  namelimit: { type: 'string', description: 'maximum visible length of the name', default: 25 },

  eventFileClear: { type: 'function', description: 'called when clear button clicked' },
  eventFileEdit: { type: 'function', description: 'called when edit button clicked' },
  eventFileClicked: { type: 'function', description: 'called when image is clicked' },
}, meleElement._properties)

export default class meleFile extends meleElement {
  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description Creates internal DOM structure.
  * Note: Preview elements (_elImg/_elIcon) are NOT created here - they're created on-demand in blobChanged()
  */
  init() {
    // Action buttons (download, edit, clear)
    this._elActions = this.appendChildren({
      tag: 'mele-file-actions',
      children: [
        { tag: 'mele-button', key: 'edit', type: 'iconmini', iconleft: 'u.edit.note', tooltip: 'wf.generic.action.EDIT', eventButtonClicked: this.eventFileEdit.bind(this) },
        { tag: 'mele-button', key: 'clear', type: 'iconmini', iconleft: 'u.clear', tooltip: 'form.clear', eventButtonClicked: this.eventFileClear.bind(this) },
      ],
    })

    // File metadata display
    this._elInfo = this.appendChildren({ tag: 'mele-file-info' })
    this._elName = this._elInfo.appendChildren({ tag: 'mele-file-name' })
    this._elTimestamp = this._elInfo.appendChildren({ tag: 'mele-file-create' })
    this._elSize = this._elInfo.appendChildren({ tag: 'mele-file-size' })

    this.onclick = this.eventFileClicked.bind(this)
  } // init

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description Handle change of attribute "name"
  * Truncates long filenames intelligently (preserves extension)
  */
  nameChanged(value) {
    if(!value) return
    const limit = Number(this.namelimit) || 25
    const dot = value.lastIndexOf('.')

    // If file has extension, preserve it while truncating base name
    if(dot > 0 && dot < value.length - 1) {
      const base = value.slice(0, dot)
      const ext = value.slice(dot) // includes dot
      const room = Math.max(3, limit - ext.length) // keep at least some base
      const shownBase = base.length > room ? (base.slice(0, room - 1) + '…') : base
      this._elName.textContent = shownBase + ext
    } else {
      // No extension, simple truncation
      this._elName.textContent = value.length > limit ? (value.slice(0, limit - 1) + '…') : value
    }

    // Full name on hover
    this.setAttribute('title', value)
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description Handle change of attribute "mime"
  */
  mimeChanged(value) {
    if(value) {
      // @TODO draw icons directly based from mimetype
    }
  } // mimeChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description Handle change of attribute "size"
  * Formats byte size into human-readable format
  */
  sizeChanged(value) {
    if(value) {
      this._elSize.textContent = this._formatBytes(value)
    }
  } // sizeChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description Handle change of attribute "timestamp"
  * Formats timestamp into readable date
  */
  timestampChanged(value) {
    if(value) {
      const ts = Number(value)
      // Handle seconds, milliseconds or microseconds
      const ms = ts < 1e12 ? (ts < 1e10 ? ts * 1000 : ts) : Math.floor(ts / 1000)
      this._elTimestamp.textContent = moment(ms).format('DD.MM.YYYY HH:mm') // 24h format
    }
  } // timestampChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description Handle change of attribute "blob"
  * Creates appropriate preview (image, PDF icon, or generic icon) based on mime type
  */
  blobChanged(value) {
    if (!value) return

    // Clean up any existing preview elements
    this._cleanupPreview()

    // IMAGE PREVIEW - show actual image
    if (/image/.test(this.mime)) {
      this._pdfUri = null
      this._createImagePreview(value)
      return
    }

    // PDF PREVIEW - show icon, but store URI for modal
    if (/pdf/.test(this.mime)) {
      this._pdfUri = value
      const [, subtype] = (this.mime || '').split('/')
      this._createIconPreview(subtype || 'pdf')
      return
    }

    // OTHER FILE TYPES - show generic icon
    this._pdfUri = null
    const [, subtype] = (this.mime || '').split('/')
    this._createIconPreview(subtype || 'generic')
  } // blobChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description Removes existing preview elements (_elImg and _elIcon)
  * Called before creating new preview to avoid duplicates
  */
  _cleanupPreview() {
    if (this._elImg) {
      this._elImg.deleteElement()
      delete this._elImg
    }
    if (this._elIcon) {
      this._elIcon.deleteElement()
      delete this._elIcon
    }
  } // _cleanupPreview

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description Creates an image preview element with click handler
  * @param {string} src - The image source URL or blob
  */
  _createImagePreview(src) {
    this._elImg = this.appendChildren({
      tag: 'img',
    })
    this._elImg.src = src
  } // _createImagePreview

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description Creates an icon preview element with click handler
  * @param {string} type - The icon type/subtype (e.g., 'pdf', 'doc', 'generic')
  */
  _createIconPreview(type = 'generic') {
    this._elIcon = this.prependChildren({
      tag: 'mele-image',
      icon: 'file.' + type,
      dimensions: this.dimensions,
    })
  } // _createIconPreview

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description Opens PDF in fullscreen modal with iframe
  * @param {string} src - The PDF source URL
  */
  _openPdfModal(src) {
    if(!src) return
    this.modalOpen({
      key: 'pdfpreview',
      position: 'full',
      items: [
        { tag: 'iframe', class: 'iframe', src }, // full PDF, no page restriction
      ],
      footerbuttons: ['cancel'],
    })
  } // _openPdfModal

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description Draws image preview from blob into canvas. Adjusts the preview by ratio of canvas
  * @param {HTMLCanvasElement} canvas - The canvas element to draw on
  */
  _drawImage(canvas) {
    const img = this._img
    const ctx = canvas.getContext('2d')

    // Calculate aspect-ratio-preserving dimensions centered in canvas
    // @TODO: Make ratio and position calculation 100% accurate on all file types
    const ratio = Math.min(canvas.width / img.width, canvas.height / img.height)
    const posX = (canvas.width / 2) - (img.width / 2) * ratio
    const posY = (canvas.height / 2) - (img.height / 2) * ratio

    ctx.drawImage(img, posX, posY, img.width * ratio, img.height * ratio)

    // Help GC
    delete this._img
  } // _drawImage

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description default handling of eventFileClear
  * Removes this file from its parent container
  */
  eventFileClear() {
    const holder = this.closest('.mele-container:not([propagate])')
    if(holder) holder.removeItems(this.key, true)
  } // eventFileClear

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description default handling of eventFileEdit
  * Opens modal to edit file name
  */
  eventFileEdit() {
    const holder = this.closest('.mele-container:not([propagate])')
    const elModal = this.modalOpen({
      key: ':input',
      class: 'editfile',
      outclick: ':modal.cancel',
      position: 'center',
      footerbuttons: ['cancel', 'save'],
      header: 'Edit File',
      formCancel: ()=>{ elModal.modalClose() },
      formSubmit: ()=>{
        const values = elModal.getValues()
        elModal.modalClose()
        holder.updateItems({
          key: this.key,
          name: values.name,
        })
      },
    })
    elModal._body.appendChildren([
      { tag: 'mele-input', type: 'string', label: 'File name', key: 'name', value: this.name },
    ])
  } // eventFileEdit


  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description default handling of eventFileClicked
  * For PDFs: opens in fullscreen modal
  * For images: opens in canvas modal with brightness control
  */
  eventFileClicked() {
    // PDF path - open in iframe modal
    if(/pdf/.test(this.mime) && this._pdfUri) {
      this._openPdfModal(this._pdfUri)
      return
    }

    // Image path - open in canvas with brightness control
    const elModal = this.modalOpen({
      key: ':preview',
      class: 'canvas',
      outclick: ':modal.cancel',
    })

    const elCanvas = elModal._body.appendChildren({ tag: 'canvas' })
    elCanvas.onclick = ()=>elModal.modalClose()

    // Add brightness filter control
    elModal._body.appendChildren([
      {
        tag: 'div',
        class: 'brightness',
        children: [
          { tag: 'label', for: 'filter', html: 'brightness filter' },
          {
            tag: 'input',
            name: 'filter',
            type: 'range',
            min: '0',
            max: '200',
            step: '10',
            value: this._brightness || 100,
            onchange: (e)=>{
              const filter = `brightness(${e.target.value}%)`
              elCanvas.style.filter = filter
            },
          },
        ],
      },
    ])

    // Size canvas to fit window with padding
    elCanvas.width = window.innerWidth - 64
    elCanvas.height = window.innerHeight - 64

    // Load and draw image
    const img = (this._img = new Image())
    img.onload = this._drawImage.bind(this, elCanvas)
    // Use existing img element source if available, else use blob
    img.src = this._elImg?.src || this.blob
  } // eventFileClicked

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description Formats Byte number into human readable form
  * @param {number} bytes - The number of bytes
  * @returns {string} Formatted string (e.g., "1.23 MB")
  */
  _formatBytes(bytes) {
    if(bytes < 1024) return bytes + ' Bytes'
    else if(bytes < 1048576) return(bytes / 1024).toFixed(2) + ' KB'
    else if(bytes < 1073741824) return(bytes / 1048576).toFixed(2) + ' MB'
    else return(bytes / 1073741824).toFixed(2) + ' GB'
  } // _formatBytes

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description Clears ObjectUrl of files when item is removed from DOM
  * Prevents memory leaks from blob URLs
  */
  disconnectedCallback() {
    if(this.isParkedDisconnect && this.isParkedDisconnect()) return
    if(this.blob && /^blob:/.test(this.blob)) {
      try { URL.revokeObjectURL(this.blob) } catch(e) {}
    }
  } // disconnectedCallback
} // class meleFile

meleFile._properties = PROPERTIES
meleFile.observedAttributes = _.keys(PROPERTIES)

window.customElements.define('mele-file', meleFile)
